/**
 * Sidebar.js - Menu Lateral Premium Responsivo
 * CG Business ERP - Com Logo Oficial, Cores Corporativas e Suporte Mobile
 * 
 * Cores Corporativas:
 * - Laranja: #F6931F
 * - Azul: #2B4DAA
 */

import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useLanguage } from '@/i18n';
import { cn } from '@/lib/utils';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Wrench,
  ShoppingBag,
  DollarSign,
  Users,
  BarChart3,
  MessageSquare,
  Settings,
  LogOut,
  FolderTree,
  Receipt,
  Wallet,
  UserCircle,
  Bell,
  Key,
  Shield,
  ShieldCheck,
  TrendingUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Boxes,
  FileText,
  Crown,
  Info,
  Menu,
  X,
  Building2,
  HardHat,
  ArrowRightLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import NotificationBell from '@/components/NotificationBell';
import LanguageSelector from '@/components/LanguageSelector';
import FeedbackQuickModal from '@/components/FeedbackQuickModal';
import { inventoryAPI, manualAPI } from '@/lib/api';
import { useState, useEffect, useMemo, useCallback, createContext, useContext, useRef } from 'react';

// Corporate Colors
const COLORS = {
  orange: '#F6931F',
  blue: '#2B4DAA',
  blueDark: '#1E3A8A',
  blueDeep: '#1E2F5A'
};

// Sidebar Context for global state
const SidebarContext = createContext(null);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    return { isCollapsed: false, isMobileOpen: false, toggleCollapse: () => {}, toggleMobile: () => {} };
  }
  return context;
};

// Menu structure with groups
// Roles disponíveis: admin, store_manager, supervisor, staff, sales, cashier, 
// receptionist, technician, tech_lead, accountant, warehouse, procurement, 
// customer_support, driver, marketing, intern
const getMenuStructure = (t) => [
  {
    group: t('menu.panel'),
    items: [
      { key: 'dashboard', href: '/dashboard', icon: LayoutDashboard, roles: ['admin', 'store_manager', 'staff', 'technician', 'accountant', 'sales', 'cashier'], permission: 'dashboard.view', highlight: true },
      { key: 'financialAnalysis', href: '/financial-analysis', icon: TrendingUp, roles: ['admin', 'store_manager', 'accountant'], permission: 'financial_analysis.view' },
      { key: 'riskManagement', href: '/risk-management', icon: Shield, roles: ['admin'], permission: 'risk_management.view' },
    ]
  },
  {
    group: t('menu.operations'),
    items: [
      { key: 'inventory', href: '/inventory', icon: Package, roles: ['admin', 'store_manager', 'staff', 'warehouse'], permission: 'inventory.view' },
      { key: 'kits', href: '/kits', icon: Boxes, roles: ['admin', 'store_manager', 'staff', 'warehouse'] },
      { key: 'sales', href: '/sales?tab=sales', icon: ShoppingCart, roles: ['admin', 'store_manager', 'staff', 'sales', 'cashier'], permission: 'sales.view' },
      { key: 'documents', href: '/documents', icon: FileText, roles: ['admin', 'store_manager', 'staff', 'accountant'], permission: 'documents.view' },
      { key: 'stockTransfers', href: '/stock-transfers', icon: ArrowRightLeft, roles: ['admin', 'store_manager', 'supervisor', 'warehouse'], permission: 'inventory.transfer' },
      { key: 'services', href: '/services', icon: Wrench, roles: ['admin', 'store_manager', 'technician', 'tech_lead', 'staff', 'cashier', 'receptionist'], permission: 'services.view' },
      // Obras/Projectos agora está dentro de Serviços como módulo 'obras' (/services?module=obras)
      { key: 'procurement', href: '/procurement', icon: ShoppingBag, roles: ['admin', 'store_manager', 'procurement'], permission: 'procurement.view' },
    ]
  },
  {
    group: t('menu.financial'),
    items: [
      { key: 'cash', href: '/sales?tab=cash', icon: Wallet, roles: ['admin', 'store_manager', 'accountant', 'cashier'], permission: 'cash.view' },
      { key: 'receivables', href: '/receivables', icon: FileText, roles: ['admin', 'store_manager', 'accountant'], permission: 'receivables.view' },
      { key: 'expenses', href: '/expenses', icon: Receipt, roles: ['admin', 'store_manager', 'accountant'], permission: 'expenses.view' },
      { key: 'payroll', href: '/payroll', icon: DollarSign, roles: ['admin', 'accountant'], permission: 'payroll.view' },
      { key: 'pricing', href: '/pricing', icon: TrendingUp, roles: ['admin', 'store_manager', 'procurement'], permission: 'pricing.view' },
    ]
  },
  {
    group: t('menu.relations'),
    items: [
      { key: 'clients', href: '/clients', icon: UserCircle, roles: ['admin', 'store_manager', 'staff', 'sales', 'customer_support'], permission: 'clients.view' },
      { key: 'suppliers', href: '/suppliers', icon: Building2, roles: ['admin', 'store_manager', 'procurement'], permission: 'suppliers.view' },
      { key: 'licenses', href: '/licenses', icon: Key, roles: ['admin', 'store_manager', 'staff'], permission: 'licenses.view' },
      { key: 'alerts', href: '/alerts', icon: Bell, roles: ['admin', 'store_manager', 'supervisor'], permission: 'alerts.view' },
    ]
  },
  {
    group: t('menu.system'),
    items: [
      { key: 'catalogs', href: '/catalogs', icon: FolderTree, roles: ['admin', 'store_manager'], permission: 'catalogs.view' },
      { key: 'adminCenter', href: '/admin-center', icon: Crown, roles: ['admin', 'store_manager'], permission: 'users.view' },
      { key: 'whatsapp', href: '/whatsapp', icon: MessageSquare, roles: ['admin', 'store_manager'], permission: 'whatsapp.view' },
      { key: 'reports', href: '/reports', icon: BarChart3, roles: ['admin', 'store_manager', 'accountant', 'supervisor'], permission: 'reports.view' },
      { key: 'feedback', href: '/feedback', icon: MessageSquare, roles: ['admin', 'store_manager', 'supervisor', 'staff', 'sales', 'cashier', 'receptionist', 'technician', 'tech_lead', 'accountant', 'warehouse', 'procurement'], permission: 'feedback.view' },
      { key: 'integrity', href: '/integrity', icon: ShieldCheck, roles: ['admin', 'store_manager'], permission: 'integrity.view' },
      { key: 'settings', href: '/settings', icon: Settings, roles: ['admin'], permission: 'settings.view' },
      { key: 'about', href: '/about', icon: Info, roles: ['admin', 'store_manager', 'supervisor', 'staff', 'sales', 'cashier', 'receptionist', 'technician', 'tech_lead', 'accountant', 'warehouse', 'procurement', 'customer_support', 'driver', 'marketing', 'intern'], permission: 'about.view' },
    ]
  }
];

// Badge Component
const ItemBadge = ({ text, collapsed }) => {
  const isNew = text === 'NOVO';
  if (collapsed) return null;
  return (
    <span className={cn(
      "ml-auto px-1.5 py-0.5 text-[10px] font-bold rounded",
      isNew 
        ? "bg-emerald-500 text-white animate-pulse" 
        : "bg-[#F6931F] text-white"
    )}>
      {text}
    </span>
  );
};

const formatCompactBadgeValue = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return null;
  if (numeric < 1000) return String(numeric);
  return new Intl.NumberFormat('pt-PT', { notation: 'compact', maximumFractionDigits: 1 }).format(numeric);
};

// Mobile Header Component
export function MobileHeader({ onMenuClick }) {
  return (
    <div 
      className="lg:hidden fixed top-0 left-0 right-0 z-[60] flex items-center justify-between px-4"
      style={{
        background: `linear-gradient(90deg, ${COLORS.blue} 0%, ${COLORS.blueDark} 100%)`,
        height: 'calc(56px + env(safe-area-inset-top, 0px))',
        paddingTop: 'env(safe-area-inset-top, 0px)',
      }}
    >
      <button
        type="button"
        onClick={onMenuClick}
        className="touch-manipulation p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
        data-testid="mobile-menu-btn"
      >
        <Menu className="h-6 w-6" />
      </button>
      <img 
        src="/images/logo-cg.png" 
        alt="CG Business" 
        className="h-8 w-auto"
      />
      <div className="flex items-center gap-1">
        <FeedbackQuickModal compact />
        <NotificationBell />
      </div>
    </div>
  );
}

export default function Sidebar({ isCollapsed, onToggleCollapse, isMobile, isMobileOpen, onCloseMobile }) {
  const location = useLocation();
  const { user, logout, hasRole, hasPermission } = useAuth();
  const { t, tNav, tAction, language } = useLanguage();
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [currentTime, setCurrentTime] = useState(new Date());
  const [inventoryBadge, setInventoryBadge] = useState(null);
  const [systemVersionBadge, setSystemVersionBadge] = useState('v3.5.0');
  const previousPathnameRef = useRef(location.pathname);

  const loadInventoryBadge = useCallback(async () => {
    if (!hasPermission('inventory.view')) {
      setInventoryBadge(null);
      return;
    }

    try {
      const response = await inventoryAPI.getSummary();
      setInventoryBadge(formatCompactBadgeValue(response.data?.total_quantity ?? 0));
    } catch {
      setInventoryBadge(null);
    }
  }, [hasPermission]);

  const loadSystemVersion = useCallback(async () => {
    try {
      const response = await manualAPI.getInfo();
      const info = response?.data || {};
      const semanticVersion = info.semantic_version || info.version;
      const dateVersion = info.date_version;
      if (semanticVersion && dateVersion) {
        setSystemVersionBadge(`v${semanticVersion} • ${dateVersion}`);
        return;
      }
      if (semanticVersion) {
        setSystemVersionBadge(`v${semanticVersion}`);
      }
    } catch {
      setSystemVersionBadge('v3.5.0');
    }
  }, []);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    if (previousPathnameRef.current !== location.pathname && isMobileOpen && onCloseMobile) {
      onCloseMobile();
    }
    previousPathnameRef.current = location.pathname;
  }, [location.pathname, isMobileOpen, onCloseMobile]);

  useEffect(() => {
    loadInventoryBadge();
  }, [loadInventoryBadge]);

  useEffect(() => {
    loadSystemVersion();
  }, [loadSystemVersion]);

  const menuStructure = useMemo(() => getMenuStructure(t), [t]);

  const toggleGroup = useCallback((group) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [group]: !prev[group]
    }));
  }, []);

  const isActive = (href) => {
    const [path, rawQuery = ''] = href.split('?');
    const pathMatch = location.pathname === path || location.pathname.startsWith(path + '/');
    if (!pathMatch) return false;

    if (!rawQuery) return true;

    const currentParams = new URLSearchParams(location.search);
    const targetParams = new URLSearchParams(rawQuery);
    return Array.from(targetParams.entries()).every(([key, value]) => currentParams.get(key) === value);
  };

  // Role badge config
  const getRoleBadge = useCallback((role) => {
    const roles = {
      admin: { label: t('roles.admin'), color: 'bg-[#F6931F]' },
      store_manager: { label: t('roles.store_manager'), color: 'bg-[#2B4DAA]' },
      accountant: { label: t('roles.accountant'), color: 'bg-emerald-500' },
      seller: { label: t('roles.seller'), color: 'bg-amber-500' },
      technician: { label: t('roles.technician'), color: 'bg-cyan-500' },
      staff: { label: t('roles.staff'), color: 'bg-slate-500' },
      cashier: { label: t('roles.cashier'), color: 'bg-pink-500' }
    };
    return roles[role] || { label: role, color: 'bg-slate-500' };
  }, [t]);

  const roleBadge = useMemo(() => getRoleBadge(user?.role), [getRoleBadge, user?.role]);

  // Sidebar content
  const sidebarContent = (
    <>
      {/* Header with Logo */}
      <div className={cn(
        "flex items-center border-b border-white/10",
        isCollapsed ? "h-16 justify-center px-2" : "h-20 justify-between px-4"
      )}>
        {!isCollapsed ? (
          <>
            <div className="flex items-center gap-2">
              <img 
                src="/images/logo-cg.png" 
                alt="CG Business" 
                className="h-12 w-auto"
                data-testid="sidebar-logo"
              />
            </div>
            <div className="flex items-center gap-1">
              <LanguageSelector />
              <FeedbackQuickModal compact />
              <NotificationBell />
            </div>
          </>
        ) : (
          <img 
            src="/images/logo-cg.png" 
            alt="CG" 
            className="h-8 w-8 object-contain"
          />
        )}
      </div>
      
      {/* Toggle Button - Desktop */}
      <button
        onClick={onToggleCollapse}
        className="hidden lg:flex absolute -right-3 top-24 z-50 h-6 w-6 items-center justify-center rounded-full bg-[#F6931F] text-white shadow-lg hover:bg-[#E8850F] transition-colors"
        data-testid="sidebar-toggle"
      >
        {isCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
      </button>

      {/* Close Button - Mobile */}
      {isMobileOpen && (
        <button
          onClick={onCloseMobile}
          className="lg:hidden absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-lg"
          data-testid="sidebar-mobile-close"
        >
          <X className="h-5 w-5" />
        </button>
      )}
      
      {/* Version Badge */}
      {!isCollapsed && (
        <div className="px-4 py-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-blue-200" data-testid="sidebar-system-version-badge">ERP {systemVersionBadge}</span>
          </div>
        </div>
      )}
      
      {/* Navigation */}
      <div className="flex-1 overflow-y-auto py-2 scrollbar-thin scrollbar-thumb-white/10">
        {menuStructure.map((section) => {
          const visibleItems = section.items.filter((item) => {
            const roleAccess = item.roles ? hasRole(item.roles) : true;
            const permissionAccess = item.permission ? hasPermission(item.permission) : true;
            return roleAccess && permissionAccess;
          });
          if (visibleItems.length === 0) return null;

          const isGroupCollapsed = collapsedGroups[section.group];

          return (
            <div key={section.group} className="mb-1">
              {/* Group Header */}
              {!isCollapsed ? (
                <button
                  onClick={() => toggleGroup(section.group)}
                  className="flex w-full items-center justify-between px-4 py-2 text-[10px] font-semibold text-[#F6931F] uppercase tracking-wider hover:text-[#FFB347] transition-colors"
                >
                  <span>{section.group}</span>
                  <ChevronDown 
                    className={cn(
                      "h-3 w-3 transition-transform duration-200",
                      isGroupCollapsed && "-rotate-90"
                    )} 
                  />
                </button>
              ) : (
                <div className="h-2" />
              )}

              {/* Group Items */}
              {!isGroupCollapsed && (
                <nav className={cn("space-y-0.5", isCollapsed ? "px-1" : "px-2")}>
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    const resolvedBadge = item.key === 'inventory' ? inventoryBadge : item.badge;
                    
                    return (
                      <Link
                        key={item.key}
                        to={item.href}
                        data-testid={`nav-${item.key}`}
                        title={isCollapsed ? tNav(item.key) : undefined}
                        className={cn(
                          'group flex items-center gap-3 rounded-lg text-sm font-medium transition-all duration-200',
                          isCollapsed ? 'justify-center p-2' : 'px-3 py-2',
                          active
                            ? 'bg-[#F6931F] text-white shadow-lg shadow-[#F6931F]/30'
                            : 'text-blue-100 hover:bg-white/10 hover:text-white'
                        )}
                      >
                        <Icon className={cn(
                          "h-4 w-4 transition-transform group-hover:scale-110",
                          active && "text-white"
                        )} strokeWidth={1.5} />
                        {!isCollapsed && (
                          <>
                            <span className="flex-1">{tNav(item.key)}</span>
                            {resolvedBadge && <ItemBadge text={resolvedBadge} collapsed={isCollapsed} />}
                          </>
                        )}
                      </Link>
                    );
                  })}
                </nav>
              )}
            </div>
          );
        })}
      </div>

      {/* Live Status */}
      {!isCollapsed && (
        <div className="px-4 py-2 border-t border-white/10">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
              <span className="text-emerald-400">{t('sidebar.systemOnline')}</span>
            </div>
            <span className="text-blue-200">
              {currentTime.toLocaleTimeString(language === 'en' ? 'en-GB' : 'pt-PT', {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: 'Africa/Maputo'
              })}
            </span>
          </div>
        </div>
      )}

      {/* User Section */}
      <div className={cn("border-t border-white/10", isCollapsed ? "p-2" : "p-3")}>
        {!isCollapsed ? (
          <>
            <div className="mb-3 rounded-lg bg-white/5 p-3 backdrop-blur">
              <div className="flex items-center gap-3">
                <div 
                  className="h-10 w-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                  style={{ background: `linear-gradient(135deg, ${COLORS.orange} 0%, #E8850F 100%)` }}
                >
                  {user?.full_name?.charAt(0) || 'U'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate text-white" data-testid="user-name">
                    {user?.full_name}
                  </p>
                  <p className="text-[10px] text-blue-200 truncate">{user?.email}</p>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className={cn(
                  "px-2 py-0.5 text-[10px] font-bold rounded-full text-white",
                  roleBadge.color
                )}>
                  {roleBadge.label}
                </span>
                {user?.role === 'admin' && (
                  <Crown className="h-3.5 w-3.5 text-[#F6931F]" />
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-blue-100 hover:text-white hover:bg-white/10"
              onClick={logout}
              data-testid="logout-button"
            >
              <LogOut className="mr-2 h-4 w-4" />
              {tAction('logout') || 'Terminar Sessão'}
            </Button>
          </>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <div 
              className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
              style={{ background: `linear-gradient(135deg, ${COLORS.orange} 0%, #E8850F 100%)` }}
              title={user?.full_name}
            >
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <button
              onClick={logout}
              className="p-2 text-blue-100 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              title={tAction('logout') || 'Terminar Sessão'}
              data-testid="logout-button-collapsed"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-[55]"
          onClick={onCloseMobile}
          data-testid="sidebar-mobile-overlay"
        />
      )}

      {/* Sidebar - Desktop */}
      {!isMobile && (
        <div 
          className={cn(
            "hidden lg:flex h-screen flex-col text-white relative transition-all duration-300",
            isCollapsed ? "w-16" : "w-64"
          )}
          style={{ 
            background: `linear-gradient(180deg, ${COLORS.blue} 0%, ${COLORS.blueDark} 50%, ${COLORS.blueDeep} 100%)`
          }}
          data-testid="sidebar"
        >
          {sidebarContent}
        </div>
      )}

      {/* Sidebar - Mobile */}
      {isMobile && (
        <div 
          className={cn(
            "lg:hidden fixed inset-y-0 left-0 z-[70] w-72 max-w-[85vw] flex flex-col text-white transition-transform duration-300",
            isMobileOpen ? "translate-x-0" : "-translate-x-full"
          )}
          style={{ 
            background: `linear-gradient(180deg, ${COLORS.blue} 0%, ${COLORS.blueDark} 50%, ${COLORS.blueDeep} 100%)`
          }}
          data-testid="sidebar-mobile-panel"
        >
          {sidebarContent}
        </div>
      )}
    </>
  );
}
